import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { StatusAlertQuery, StatusAlertService } from '@y42/alert';
import { ExportQuery, ExportService } from '@y42/data-export';
import { DataLineageQuery, DataLineageService } from '@y42/data-lineage';
import { ErrorHandlerService } from '@y42/error-handling';
import {
  AbstractFeatureAggregationService,
  AbstractFeatureEntityState,
  AbstractFeatureQueryEntity,
  AbstractFeatureService,
  Noun,
  ServiceDefinitionWithName,
  Y42FeatureTypeToChildLabel,
  Y42FeatureTypeToChildLogo,
  Y42FeatureTypeToLabel,
  Y42FeatureTypeToLogo,
} from '@y42/feature';
import { getY42GitPath, Y42GitFolders } from '@y42/feature/git';
import { Y42RouterService } from '@y42/feature/routing';
import { IntegrationQuery, IntegrationService } from '@y42/integration';
import { Y42FeatureType } from '@y42/json-schema';
import { OrchestrationQuery, OrchestrationService } from '@y42/orchestration';
import { SqlModelingQuery, SqlModelingService } from '@y42/sql-model';
import { UiModelingQuery, UiModelingService } from '@y42/ui-model';
import { VisualizationDashboardQuery, VisualizationDashboardService } from '@y42/visualization/dashboard';
import { VisualizationFilterQuery, VisualizationFilterService } from '@y42/visualization/filter';
import { VisualizationThemeQuery, VisualizationThemeService } from '@y42/visualization/theme';
import { VisualizationWidgetQuery, VisualizationWidgetService } from '@y42/visualization/widget';
import { CatalogQuery } from '../catalog/state/catalog/catalog.query';
import { CatalogService } from '../catalog/state/catalog/catalog.service';
import { Y42FeatureChildAggregationService } from './y42-feature-child-aggregation.service';

export interface FeatureServiceDefinition
  extends ServiceDefinitionWithName<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AbstractFeatureService<AbstractFeatureEntityState<any>>,
    AbstractFeatureQueryEntity
  > {}

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class Y42FeatureAggregationService extends AbstractFeatureAggregationService<
  AbstractFeatureService,
  AbstractFeatureQueryEntity
> {
  constructor(
    protected readonly routerService: Y42RouterService,
    private readonly featureChildAggregationService: Y42FeatureChildAggregationService,
    private readonly errorHandlerService: ErrorHandlerService,

    private readonly integrationService: IntegrationService,
    private readonly integrationQuery: IntegrationQuery,

    private readonly uiModelingService: UiModelingService,
    private readonly uiModelingQuery: UiModelingQuery,

    private readonly sqlModelingService: SqlModelingService,
    private readonly sqlModelingQuery: SqlModelingQuery,

    private readonly orchestrationService: OrchestrationService,
    private readonly orchestrationQuery: OrchestrationQuery,

    private readonly visualizationDashboardService: VisualizationDashboardService,
    private readonly visualizationDashboardQuery: VisualizationDashboardQuery,

    private readonly visualizationWidgetService: VisualizationWidgetService,
    private readonly visualizationWidgetQuery: VisualizationWidgetQuery,

    private readonly visualizationThemeService: VisualizationThemeService,
    private readonly visualizationThemeQuery: VisualizationThemeQuery,

    private readonly visualizationFilterService: VisualizationFilterService,
    private readonly visualizationFilterQuery: VisualizationFilterQuery,

    private readonly exportService: ExportService,
    private readonly exportQuery: ExportQuery,

    private readonly dataLineageService: DataLineageService,
    private readonly dataLineageQuery: DataLineageQuery,

    private readonly statusAlertService: StatusAlertService,
    private readonly statusAlertQuery: StatusAlertQuery,

    private readonly catalogService: CatalogService,
    private readonly catalogQuery: CatalogQuery,
  ) {
    super(routerService);
  }

  readonly featureTypeToServiceDefinition = {
    [Y42FeatureType.Integration]: {
      service: this.integrationService,
      query: this.integrationQuery,
    },
    [Y42FeatureType.UiModel]: {
      service: this.uiModelingService,
      query: this.uiModelingQuery,
    },
    [Y42FeatureType.SqlModel]: {
      service: this.sqlModelingService,
      query: this.sqlModelingQuery,
    },
    [Y42FeatureType.Orchestration]: {
      service: this.orchestrationService,
      query: this.orchestrationQuery,
    },
    [Y42FeatureType.VisualizationDashboard]: {
      service: this.visualizationDashboardService,
      query: this.visualizationDashboardQuery,
    },
    [Y42FeatureType.VisualizationWidget]: {
      service: this.visualizationWidgetService,
      query: this.visualizationWidgetQuery,
    },
    [Y42FeatureType.VisualizationTheme]: {
      service: this.visualizationThemeService,
      query: this.visualizationThemeQuery,
    },
    [Y42FeatureType.VisualizationFilter]: {
      service: this.visualizationFilterService,
      query: this.visualizationFilterQuery,
    },
    [Y42FeatureType.Export]: {
      service: this.exportService,
      query: this.exportQuery,
    },
    [Y42FeatureType.Lineage]: {
      service: this.dataLineageService,
      query: this.dataLineageQuery,
    },
    [Y42FeatureType.StatusAlert]: {
      service: this.statusAlertService,
      query: this.statusAlertQuery,
    },
    [Y42FeatureType.Catalog]: {
      service: this.catalogService,
      query: this.catalogQuery,
    },
  } as const;

  getServiceDefinitionByPath($path: string) {
    const gitFolders = Object.values(Y42GitFolders).sort((a, b) => b.split('/').length - a.split('/').length);
    const gitFolder = gitFolders.find((folder) => $path.includes(folder))!;

    // src/Integrations/integration_one/metadata.json
    // src/Models/SQL/model_one/metadata.json
    const pathElements = $path.replaceAll(getY42GitPath(gitFolder), '').split('/').filter(Boolean);

    // integration_one
    // integration_one/metadata.json
    // model_one
    // model_one/metadata.json
    const isParent =
      pathElements.length && pathElements.length <= 2 && (!pathElements[1] || pathElements[1]?.endsWith('.json'));

    // integration_one/table_one
    // integration_one/table_one/metadata.json
    // model_one/table_one
    // model_one/table_one/metadata.json
    const isChild =
      pathElements.length && pathElements.length <= 3 && (!!pathElements[1] || !pathElements[1]?.endsWith('.json'));

    if (isParent) {
      return {
        ...this.getByGitPath($path)!,
        level: 0,
      };
    } else if (isChild) {
      return {
        ...this.featureChildAggregationService.getByGitPath($path)!,
        level: 1,
      };
    }

    this.errorHandlerService.handleError(
      `[FeatureAggregationService.getServiceDefinitionByPath]: could not find def for path "${$path}"`,
    );
    return null;
  }

  /**
   * Finds the top-level feature's label for the given path, even if the path points to a child entity.
   *
   * Example: `src/Models/SQL/sql-model/table` would return "SQL-Models"
   */
  getRootLabelByPath($path: string, noun?: Noun): string | null {
    const serviceDef = this.getByGitPath($path);
    if (!serviceDef) {
      return null;
    }

    return this.getLabelByFeatureType(serviceDef.featureType, 0, noun);
  }

  /**
   * Finds the top-level feature's logo for the given path, even if the path points to a child entity.
   *
   * Example: `src/Models/SQL/sql-model/table` would return "assets/images/y42-product-logos/y42-model-sql.svg"
   */
  getRootLogoByPath($path: string): string | null {
    const serviceDef = this.getByGitPath($path);
    if (!serviceDef) {
      return null;
    }

    return this.getLogoByFeatureType(serviceDef.featureType);
  }

  /**
   * Finds the feature's label for the given path.
   *
   * Example: `src/Models/SQL/sql-model/table` would return "Tables"
   */
  getLabelByPath($path: string, noun?: Noun): string | null {
    const serviceDef = this.getServiceDefinitionByPath($path);

    if (!serviceDef) {
      return null;
    }

    // `serviceDef.featureType` may be undefined for children entities
    const featureType = this.getByGitPath($path)!.featureType;
    return this.getLabelByFeatureType(featureType, serviceDef.level, noun);
  }

  /**
   * Returns the label for the given feature type.
   */
  getLabelByFeatureType(featureType: Y42FeatureType, level = 0, noun = Noun.Plural): string | null {
    if (level === 0) {
      return Y42FeatureTypeToLabel[featureType]?.[noun];
    } else if (level === 1) {
      return Y42FeatureTypeToChildLabel[featureType]?.[noun] || null;
    }

    return null;
  }

  /**
   * Finds the feature's logo for the given path.
   */
  getLogoByPath($path: string): string | null {
    const serviceDef = this.getServiceDefinitionByPath($path);
    if (!serviceDef) {
      return null;
    }

    const entity = serviceDef.query.getUIEntity($path);
    if (entity?.typeIcon) {
      return entity.typeIcon;
    }

    // `serviceDef.featureType` may be undefined for children entities
    const featureType = this.getByGitPath($path)!.featureType;
    return this.getLogoByFeatureType(featureType, serviceDef.level);
  }

  /**
   * Returns the logo for the given feature type.
   */
  getLogoByFeatureType(featureType: Y42FeatureType, level = 0) {
    if (level === 0) {
      return Y42FeatureTypeToLogo[featureType];
    } else if (level === 1) {
      return Y42FeatureTypeToChildLogo[featureType];
    }

    return null;
  }
}
